import { createStore } from 'vuex'
import _ from 'lodash'
import 'string_score'

import * as mutationTypes from './mutation-types'
import { SfLog } from '@app/adapters/logger'

import workflows from './workflows'
import accounts from './accounts'
import companies from './companies'
import reconciliationTexts from './reconciliation-texts'
import createQueryModule from './query'
import ui from './ui'

import { limitSearchResults, prepareData } from './helpers'

export default function createSearchStore({
  currentFirmId,
  currentCompanyId,
  templateOwnerId = null,
  templateOwnerType = null,
}) {
  return createStore({
    modules: {
      accounts,
      companies,
      reconciliationTexts,
      workflows,
      query: createQueryModule(),
      ui,
    },

    state: {
      currentFirmId,
      currentCompanyId,
      templateOwnerId,
      templateOwnerType,
      dataLoaded: false,
      dataLoading: false,
      limit: 4,
      recentCompanies: [],
      recentTemplates: [],
    },

    getters: {
      allSearchResults: (
        { dataLoaded },
        {
          processedReconciliationTexts,
          processedAccounts,
          processedCompanies,
          processedWorkflows,
          currentQuery,
          recentTemplates,
          recentCompanies,
          modalVisible,
        }
      ) => {
        if (!dataLoaded) {
          return []
        } else if (dataLoaded && _.isEmpty(currentQuery) && modalVisible) {
          return [
            ['RecentTemplate', recentTemplates],
            ['Company', recentCompanies],
          ]
        }

        return [
          ['Workflow', processedWorkflows],
          ['ReconciliationText', processedReconciliationTexts],
          ['Account', processedAccounts],
          ['Company', processedCompanies],
        ]
      },
      searchResults: (state, { allSearchResults }) => {
        const results = _.map(allSearchResults, (item) =>
          limitSearchResults(item[1], state.limit)
        )

        return _.concat(...results)
      },
      searchResultsByType:
        (state, { searchResults }) =>
        (type) => {
          return _.filter(searchResults, { type })
        },
      extraSearchResultsCountsByType:
        (state, { allSearchResults }) =>
        (type) => {
          for (const item of allSearchResults) {
            if (item[0] === type) {
              return Math.max(item[1].length - state.limit, 0)
            }
          }
        },
      recentCompanies: ({ recentCompanies }) => recentCompanies,
      recentTemplates: ({ recentTemplates }) => recentTemplates,
    },

    mutations: {
      [mutationTypes.SET_DATA_LOADING](state, dataLoading) {
        state.dataLoading = dataLoading
      },
      [mutationTypes.SET_DATA_LOADED](state) {
        state.dataLoaded = true
      },
      [mutationTypes.SET_LIMIT](state, limit) {
        state.limit = limit
      },
      [mutationTypes.SET_RECENT_COMPANIES_DATA](state, data) {
        state.recentCompanies = prepareData(data, 'Company')
      },
      [mutationTypes.SET_RECENT_TEMPLATES_DATA](state, data) {
        state.recentTemplates = data.map((template) => {
          const isAccount = template.number || template.original_number
          const type = isAccount ? 'Account' : 'ReconciliationText'
          return prepareData([template], type)[0]
        })
      },
    },

    actions: {
      fetchSearchData({ commit, state }) {
        commit(mutationTypes.SET_DATA_LOADING, true)

        const params = {
          firm_id: state.currentFirmId,
          company_id: state.currentCompanyId,
        }

        if (state.templateOwnerId && state.templateOwnerType) {
          params.template_owner_id = state.templateOwnerId
          params.template_owner_type = state.templateOwnerType
        }

        window.$.ajax({
          url: Silverfin.Routes.quick_search_path(params),
          dataType: 'json',
          error: (error) => {
            commit(mutationTypes.SET_DATA_LOADING, false)
            SfLog.warn('Failed to fetch quick search data', {
              extra: { error },
            })
          },
          success: (items) => {
            commit(mutationTypes.SET_DATA_LOADING, false)
            commit(mutationTypes.SET_DATA_LOADED)
            commit(mutationTypes.SET_COMPANIES_DATA, items.companies)
            commit(mutationTypes.SET_ACCOUNTS_DATA, items.accounts)
            commit(
              mutationTypes.SET_RECONCILIATION_TEXTS_DATA,
              items.reconciliationTexts
            )
            commit(mutationTypes.SET_WORKFLOWS_DATA, items.workflows)
            commit(
              mutationTypes.SET_RECENT_COMPANIES_DATA,
              items.recentCompanies
            )
            commit(
              mutationTypes.SET_RECENT_TEMPLATES_DATA,
              items.recentTemplates || []
            )
          },
        })
      },
      setResultLimit({ commit }, payload) {
        commit(mutationTypes.SET_LIMIT, payload)
      },
    },
  })
}
